import { observer } from "mobx-react-lite";
import { CalculatorIcon, DatabaseIcon } from "lucide-react";
import { useCallback } from "react";

import { MathOperatorConfiguration } from "@/types/evaluate";
import { BlockProps } from "@/components/PowerBlock/Block";
import { useBlock, withBlockStore } from "@/components/PowerBlock/Block/block-store";
import { createEmptySources } from "@/components/PowerBlock/Block/hooks";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { SingleSourceSelector } from "@/components/PowerBlock/Block/SourceSelectors";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { conditional, custom, required } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import Checkbox from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const operators = [
  { value: "lt", label: "Less than (<)" },
  { value: "le", label: "Less than or equal (≤)" },
  { value: "gt", label: "Greater than (>)" },
  { value: "ge", label: "Greater than or equal (≥)" },
] as const;

const validationSchema = createBlockSchema<MathOperatorConfiguration>()
  .requireName()
  .addRule(required("configuration.operator", "Operator is required"))
  .addRule(required("configuration.sources.0", "Source is required"))
  .addRule(
    conditional(
      "configuration.sources.1",
      payload =>
        payload.configuration?.sources?.[1] !== null &&
        payload.configuration?.sources?.[1] !== undefined,
      required("configuration.sources.1", "Comparison source is required"),
    ),
  )
  .addRule(
    conditional(
      "configuration.value",
      payload =>
        payload.configuration?.sources?.[1] === null ||
        payload.configuration?.sources?.[1] === undefined,
      custom(
        "configuration.value",
        value => value !== null && value !== undefined && !isNaN(Number(value)),
        "Comparison value must be a valid number",
      ),
    ),
  )
  .build();

const ComparisonOperatorBlockCore = observer(
  ({
    sources = createEmptySources(),
  }: Omit<BlockProps<MathOperatorConfiguration>, "DefaultFields">) => {
    const block = useBlock<MathOperatorConfiguration>();
    const isReadonly = useIsReadonly();

    const compareWithColumn = Boolean(
      block.configuration?.sources?.[1] !== null && block.configuration?.sources?.[1] !== undefined,
    );

    const handleOperatorChange = useCallback(
      (operator: "lt" | "le" | "gt" | "ge") => {
        block.setConfiguration({
          ...block.configuration,
          operator,
          sources: block.configuration?.sources || ["", null],
          value: block.configuration?.value ?? null,
        });
      },
      [block],
    );

    const handleValueChange = useCallback(
      (value: string) => {
        const numValue = parseFloat(value);
        block.setConfiguration({
          ...block.configuration,
          operator: block.configuration?.operator || "lt",
          sources: [block.configuration?.sources?.[0] || "", null] as [string, string | null],
          value: isNaN(numValue) ? null : numValue,
        });
      },
      [block],
    );

    const handleCompareWithColumnToggle = useCallback(
      (enabled: boolean) => {
        if (enabled) {
          // Switch to source comparison mode
          block.setConfiguration({
            ...block.configuration,
            operator: block.configuration?.operator || "lt",
            sources: [block.configuration?.sources?.[0] || "", ""] as [string, string | null],
            value: null,
          });
        } else {
          // Switch to value comparison mode
          block.setConfiguration({
            ...block.configuration,
            operator: block.configuration?.operator || "lt",
            sources: [block.configuration?.sources?.[0] || "", null] as [string, string | null],
            value: 0,
          });
        }
      },
      [block],
    );

    return (
      <>
        <SectionGroup
          title="Comparison Configuration"
          description="Configure the mathematical comparison operation"
          icon={<CalculatorIcon className="h-4 w-4" />}
        >
          <div className="space-y-4">
            <ValidatedField name="Operator" path="configuration.operator">
              <Select
                value={block.configuration?.operator || undefined}
                onValueChange={handleOperatorChange}
                disabled={isReadonly}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select an operator..." />
                </SelectTrigger>
                <SelectContent>
                  {operators.map(op => (
                    <SelectItem key={op.value} value={op.value}>
                      {op.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </ValidatedField>

            <ValidatedField name="Comparison Mode" path="">
              <Checkbox
                checked={compareWithColumn}
                onChange={e => handleCompareWithColumnToggle(e.target.checked)}
                label="Compare with another source"
                disabled={isReadonly}
              />
            </ValidatedField>
          </div>
        </SectionGroup>

        <SectionGroup
          title="Data Sources"
          description="Select the values to compare"
          icon={<DatabaseIcon className="h-4 w-4" />}
        >
          <div className="space-y-4">
            <SingleSourceSelector
              sources={sources}
              sourceKey="sources.0"
              validationPath="configuration.sources.0"
              name="Source"
            />

            {compareWithColumn ? (
              <SingleSourceSelector
                sources={sources}
                sourceKey="sources.1"
                validationPath="configuration.sources.1"
                name="Comparison Source"
              />
            ) : (
              <ValidatedField name="Comparison Value" path="configuration.value">
                <Input
                  type="number"
                  placeholder="Enter a numeric value..."
                  value={block.configuration?.value ?? ""}
                  onChange={e => handleValueChange(e.target.value)}
                  step="0.01"
                  className="w-full"
                  disabled={isReadonly}
                />
              </ValidatedField>
            )}
          </div>
        </SectionGroup>
      </>
    );
  },
);

export default withBlockStore<MathOperatorConfiguration>(ComparisonOperatorBlockCore, {
  validationSchema,
});
